import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Download, Droplets } from "lucide-react";
import { useContent, useContentReady } from "@/content/ContentContext";
import { useLang } from "@/i18n/LanguageContext";
import { slugify } from "@/lib/slug";

const RELATED_GROUPS = [
  ["hdpe-pipes", "pvc-pipes", "ldpe-laterals"],
  ["sprinkler-systems", "mini-sprinklers", "micro-sprinklers", "portable-sprinklers", "rain-guns"],
  ["drip-systems", "filters", "venturi", "drip-accessories"],
  ["fittings-valves", "pvc-fittings", "pp-compression-fittings", "pp-fittings-32mm", "ball-valves", "foot-valves", "air-release-valves", "service-saddles", "sprinkler-accessories"],
];

export default function ProductDetailPage() {
  const { id } = useParams();
  const content = useContent();
  const ready = useContentReady();
  const { lang, t } = useLang();
  const p = content.products.find((x) => x.id === id);

  if (!ready && !p) {
    return (
      <section
        data-testid="product-detail-loading"
        className="mx-auto flex max-w-[1440px] items-center justify-center px-6 pb-28 pt-32 lg:px-12 lg:pt-40"
      >
        <p className="text-sm uppercase tracking-[0.2em] text-sage">Loading…</p>
      </section>
    );
  }
  if (!p) return <Navigate to="/products" replace />;

  const group = RELATED_GROUPS.find((g) => g.includes(p.id)) || [];
  let related = group
    .filter((rid) => rid !== p.id)
    .map((rid) => content.products.find((x) => x.id === rid))
    .filter(Boolean)
    .slice(0, 4);
  if (related.length === 0) {
    related = content.products.filter((x) => x.id !== p.id).slice(0, 4);
  }

  return (
    <section
      data-testid="product-detail"
      className="mx-auto max-w-[1440px] px-6 pb-28 pt-32 lg:px-12 lg:pt-40"
    >
      <Link
        to="/products"
        data-testid="back-to-products-link"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-sage transition-colors duration-300 hover:text-harvest"
      >
        <ArrowLeft className="h-4 w-4" /> {t("allProducts")}
      </Link>

      <div className="mt-10 grid items-start gap-12 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden border border-line lg:col-span-6"
        >
          <img src={p.img} alt={p.alt} className="aspect-[4/3] w-full object-cover" />
          <span className="absolute left-5 top-5 bg-forest/70 px-3 py-1 font-display text-sm italic text-harvest backdrop-blur-md">
            {p.num}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-sage">{p.hindi}</p>
          <h1
            data-testid="product-detail-name"
            className="mt-3 font-display text-4xl font-medium tracking-tight text-cream sm:text-5xl lg:text-6xl"
          >
            {p.name}
          </h1>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-sage sm:text-base">
            {p.desc}
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {p.features.filter(Boolean).map((f) => (
              <div key={f} className="flex items-start gap-3">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-harvest" />
                <span className="text-sm leading-relaxed text-cream/80">{f}</span>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <p className="text-xs uppercase tracking-[0.2em] text-sage">{t("rangeIncludes")}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {p.range.filter(Boolean).map((r) => (
                <span
                  key={r}
                  className="border border-line bg-moss px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-cream/70"
                >
                  {r}
                </span>
              ))}
            </div>
          </div>

          {p.items && p.items.filter(Boolean).length > 0 && (
            <div className="mt-10" data-testid="product-items-list">
              <p className="text-xs uppercase tracking-[0.2em] text-sage">
                {t("itemsList")}
              </p>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {p.items.filter(Boolean).map((it) => (
                  <span
                    key={it}
                    className="border border-line bg-moss px-3 py-2 text-xs uppercase tracking-[0.12em] text-cream/75"
                  >
                    {it}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-2">
            {p.specs.filter(Boolean).map((sp) => (
              <span key={sp} className="px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-sage">
                {sp}
              </span>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="product-detail-enquire-button"
              className="group inline-flex items-center gap-3 bg-harvest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-harvestHover"
            >
              {lang === "hi" ? `${p.name} ${t("enquireFor")}` : `${t("enquireFor")} ${p.name}`}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            {p.models && p.models.length > 0 && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/catalogue/${p.id}`}
                data-testid="download-catalogue-button"
                className="group inline-flex items-center gap-3 border border-line px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:border-harvest hover:text-harvest"
              >
                {t("downloadCatalogue")}
                <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </a>
            )}
          </div>
        </motion.div>
      </div>

      {p.models && p.models.length > 0 && (
        <div className="mt-20 border-t border-line pt-14" data-testid="product-models">
          <p className="text-xs uppercase tracking-[0.3em] text-harvest">
            {t("completeRange")}
          </p>
          <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-cream sm:text-4xl">
            {p.name} — <span className="italic text-harvest">har model.</span>
          </h2>
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {p.models.map((m, i) => (
              <motion.div
                key={`${m.name}-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 5) * 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/products/${p.id}/models/${slugify(m.name)}`}
                  data-testid={`model-card-${i}`}
                  className="group block border border-line bg-white p-4 text-center transition-all duration-300 hover:-translate-y-1 hover:border-harvest"
                >
                  {m.img ? (
                    <img
                      src={m.img}
                      alt={m.name}
                      loading="lazy"
                      className="mx-auto h-32 w-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <span className="mx-auto flex h-32 w-full flex-col items-center justify-center gap-2 bg-moss text-sage">
                      <Droplets className="h-8 w-8" strokeWidth={1.25} />
                      <span className="text-[10px] uppercase tracking-[0.15em]">Photo jald aa rahi hai</span>
                    </span>
                  )}
                  <p className="mt-3 text-[11px] font-semibold uppercase leading-snug tracking-[0.1em] text-cream">
                    {m.name}
                  </p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {related.length > 0 && (
        <div className="mt-20 border-t border-line pt-14" data-testid="related-products">
          <p className="text-xs uppercase tracking-[0.3em] text-harvest">
            Related Products
          </p>
          <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-cream sm:text-4xl">
            Aur dekhein — <span className="italic text-harvest">related items.</span>
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/products/${r.id}`}
                data-testid={`related-${r.id}`}
                className="group border border-line bg-moss transition-transform duration-500 hover:-translate-y-1"
              >
                <div className="overflow-hidden">
                  <img
                    src={r.img}
                    alt={r.alt}
                    className="h-40 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <p className="font-display text-lg font-medium tracking-tight text-cream transition-colors duration-300 group-hover:text-harvest">
                    {r.name}
                  </p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-sage">
                    {r.hindi}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
